import React from 'react';
import { Flame, Scale, Utensils } from 'lucide-react';
import { getTasteLabel, getPortionLabel, getValueLabel, getTagLabel } from '@/utils/ratingUtils';
import { RatingDistributionBar } from './RatingDistributionBar';

interface DishStats {
  top_taste: string | null;
  top_portion: string | null;
  top_value: string | null;
  total_reviews: number | null;
  taste_count: number | null;
  portion_count: number | null;
  value_count: number | null;
}

interface DishDistribution {
  total_reviews: number | null;
  taste_solid_count: number | null;
  taste_fire_count: number | null;
  taste_life_changing_count: number | null;
  taste_meh_count: number | null;
  portion_just_right_count: number | null;
  portion_snack_attack_count: number | null;
  portion_food_coma_count: number | null;
  value_fair_deal_count: number | null;
  value_steal_count: number | null;
  value_splurge_count: number | null;
  value_daylight_robbery_count: number | null;
}

interface DishStatsBarProps {
  stats: DishStats | null;
  topTags: string[];
  distribution?: DishDistribution | null;
}

export const DishStatsBar: React.FC<DishStatsBarProps> = ({ stats, topTags, distribution }) => {
  const hasStats = stats && (stats.top_taste || stats.top_portion || stats.top_value);
  const hasTags = topTags.length > 0;
  const hasDistribution = distribution && (distribution.total_reviews ?? 0) > 1;

  if (!hasStats && !hasTags && !hasDistribution) {
    return (
      <div className="text-center py-4 px-2 border-t border-border mt-4">
        <p className="text-sm text-muted-foreground italic">
          No community reviews yet. Be the first to add a snap!
        </p>
      </div>
    );
  }

  const totalReviews = distribution?.total_reviews ?? 0;

  return (
    <div className="px-4 py-4 bg-muted/50 border-b border-border">
      {/* Rating Stats - Mode display for single review or when no distribution */}
      {hasStats && !hasDistribution && (
        <div className="flex flex-wrap gap-3 mb-3">
          {stats?.top_taste && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-background border border-border">
              <Flame className="h-4 w-4 text-[#cfef00]" />
              <span className="text-xs text-muted-foreground">Taste:</span>
              <span className="text-sm font-medium text-[#8aaa00] dark:text-[#cfef00]">
                {getTasteLabel(stats.top_taste)}
              </span>
            </div>
          )}
          {stats?.top_portion && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-background border border-border">
              <Utensils className="h-4 w-4 text-[#cfef00]" />
              <span className="text-xs text-muted-foreground">Portion:</span>
              <span className="text-sm font-medium text-[#8aaa00] dark:text-[#cfef00]">
                {getPortionLabel(stats.top_portion)}
              </span>
            </div>
          )}
          {stats?.top_value && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-background border border-border">
              <Scale className="h-4 w-4 text-[#cfef00]" />
              <span className="text-xs text-muted-foreground">Value:</span>
              <span className="text-sm font-medium text-[#8aaa00] dark:text-[#cfef00]">
                {getValueLabel(stats.top_value)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Percentage Breakdown - show when 2+ reviews */}
      {hasDistribution && (
        <div className="space-y-4 mb-3">
          {/* Taste Breakdown */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Flame className="h-4 w-4 text-[#cfef00]" />
              <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">Taste</h4>
            </div>
            <RatingDistributionBar label="🤯 Life-Changing" count={distribution.taste_life_changing_count ?? 0} total={totalReviews} colorClass="bg-[#cfef00]" />
            <RatingDistributionBar label="🔥 Fire" count={distribution.taste_fire_count ?? 0} total={totalReviews} colorClass="bg-[#a3be00]" />
            <RatingDistributionBar label="👍 Solid" count={distribution.taste_solid_count ?? 0} total={totalReviews} colorClass="bg-[#798e00]" />
            <RatingDistributionBar label="🤔 Meh" count={distribution.taste_meh_count ?? 0} total={totalReviews} colorClass="bg-[#4f5f00]" />
          </div>

          {/* Portion Breakdown */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Utensils className="h-4 w-4 text-[#cfef00]" />
              <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">Portion</h4>
            </div>
            <RatingDistributionBar label="👌 Just Right" count={distribution.portion_just_right_count ?? 0} total={totalReviews} colorClass="bg-[#cfef00]" />
            <RatingDistributionBar label="🍿 Snack Attack" count={distribution.portion_snack_attack_count ?? 0} total={totalReviews} colorClass="bg-[#a3be00]" />
            <RatingDistributionBar label="🤤 Food Coma" count={distribution.portion_food_coma_count ?? 0} total={totalReviews} colorClass="bg-[#798e00]" />
          </div>

          {/* Value Breakdown */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Scale className="h-4 w-4 text-[#cfef00]" />
              <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">Value</h4>
            </div>
            <RatingDistributionBar label="💎 Steal" count={distribution.value_steal_count ?? 0} total={totalReviews} colorClass="bg-[#cfef00]" />
            <RatingDistributionBar label="✅ Fair Deal" count={distribution.value_fair_deal_count ?? 0} total={totalReviews} colorClass="bg-[#a3be00]" />
            <RatingDistributionBar label="💸 Splurge" count={distribution.value_splurge_count ?? 0} total={totalReviews} colorClass="bg-[#798e00]" />
            <RatingDistributionBar label="🚨 Daylight Robbery" count={distribution.value_daylight_robbery_count ?? 0} total={totalReviews} colorClass="bg-[#4f5f00]" />
          </div>
        </div>
      )}

      {/* Top Tags */}
      {hasTags && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground text-xs">Top Tags:</span>
          <span className="text-foreground text-sm">
            {topTags.map(tag => getTagLabel(tag)).join(' • ')}
          </span>
        </div>
      )}

      {/* Review Count */}
      {stats?.total_reviews && stats.total_reviews > 0 && (
        <div className="text-xs text-muted-foreground mt-2">
          Based on {stats.total_reviews} community review{stats.total_reviews !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};
